import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CurrencyDollar, Trash, Sparkle, FilePdf, FileCsv } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatApiErrorDetail } from "../../lib/api";

const EXPENSE_CATEGORIES = [
  { value: "mileage", label: "Mileage" },
  { value: "phone", label: "Phone" },
  { value: "home_office", label: "Home Office" },
  { value: "other", label: "Other" },
];

const BLANK_EXPENSE = { category: "mileage", amount: "", description: "" };

export function RepFinancialTrackerTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [expenseForm, setExpenseForm] = useState(BLANK_EXPENSE);
  const [savingExpense, setSavingExpense] = useState(false);

  const loadSummary = () => {
    api.get("/rep/financial/summary")
      .then(({ data }) => setSummary(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      api.get(`/rep/financial/subscription/confirm?session_id=${sessionId}`)
        .then(() => {
          toast.success("Subscription activated — welcome to the Financial Tracker!");
          window.history.replaceState({}, "", window.location.pathname + "?tab=financial");
        })
        .catch(() => {})
        .finally(loadSummary);
    } else {
      loadSummary();
    }
  }, []);

  const startCheckout = async (plan) => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/rep/financial/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setCheckingOut(false);
    }
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || Number(expenseForm.amount) <= 0) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    setSavingExpense(true);
    try {
      await api.post("/rep/financial/expenses", { ...expenseForm, amount: Number(expenseForm.amount) });
      toast.success("Expense logged");
      setExpenseForm(BLANK_EXPENSE);
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingExpense(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/rep/financial/expenses/${id}`);
      toast.success("Expense removed");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadTaxSummary = async (format) => {
    const year = new Date().getFullYear();
    try {
      const { data } = await api.get(`/rep/financial/tax-summary/${format}?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `igc-tax-summary-${year}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Could not generate the tax summary — try again shortly");
    }
  };

  if (loading || !summary) return null;

  const tierLabel = summary.tier === "full_partner" ? "Full Partner" : "Starter";

  if (summary.subscription_required) {
    return (
      <div data-testid="rep-financial-tracker-tab" className="max-w-2xl">
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6 flex items-center justify-between">
          <div>
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="Starter reps earn a lower commission rate. Full Partner reps (usually after a set number of days or deals) earn a higher rate on every deal.">Your Tier</p>
            <p className="font-serif text-xl text-igc-goldText" data-testid="rep-financial-tier">{tierLabel}</p>
            {summary.tier === "starter" && summary.days_until_full_partner > 0 && (
              <p className="text-igc-muted text-xs mt-1">{summary.days_until_full_partner} days until Full Partner rate</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="Total commission from every approved deal you've ever closed, before this tracker subscription even existed.">Commission Earned</p>
            <p className="font-serif text-xl text-igc-success">${(summary.commission_totals.total_earned || 0).toLocaleString()}</p>
          </div>
        </div>

        <div className="bg-igc-surface border border-igc-gold/30 p-8 text-center" data-testid="rep-financial-upsell-card">
          <Sparkle size={32} weight="duotone" className="text-igc-goldText mx-auto mb-3" />
          <h2 className="font-serif text-2xl text-igc-cream tracking-tight mb-2">Unlock the Financial Services Tracker</h2>
          <p className="text-igc-muted text-sm mb-6 max-w-md mx-auto">Track every commission by deal, log write-offs (mileage, phone, home office), and get a live self-employment tax estimate — all in one place.</p>
          <div className="flex items-center justify-center gap-4">
            <button data-testid="rep-financial-subscribe-monthly" disabled={checkingOut} onClick={() => startCheckout("monthly")} title="Subscribe to the Financial Tracker for $25 billed every month. Cancel any time from your account settings." className="bg-igc-gold hover:bg-igc-goldHover text-black px-6 py-3 font-medium disabled:opacity-40 transition-colors">
              $25/month
            </button>
            <button data-testid="rep-financial-subscribe-yearly" disabled={checkingOut} onClick={() => startCheckout("yearly")} title="Subscribe to the Financial Tracker for $197 billed once a year — this works out cheaper than paying monthly (about 34% savings)." className="border border-igc-gold text-igc-goldText hover:bg-igc-gold/10 px-6 py-3 font-medium disabled:opacity-40 transition-colors">
              $197/year <span className="text-igc-success text-xs">(save 34%)</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rep-financial-tracker-tab" className="space-y-8 max-w-4xl">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-financial-tier-card" title="Starter reps earn a lower commission rate. Full Partner reps (after enough time/deals) earn a higher rate on every deal going forward.">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Tier</p>
          <p className="font-serif text-xl text-igc-goldText">{tierLabel}</p>
          {summary.tier === "starter" && summary.days_until_full_partner > 0 && (
            <p className="text-igc-muted text-[10px] mt-1">{summary.days_until_full_partner}d to promotion</p>
          )}
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-financial-earned" title="Total commission from every deal that has been fully approved as yours — this money is locked in.">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Earned</p>
          <p className="font-serif text-xl text-igc-success">${summary.commission_totals.total_earned.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-financial-pending" title="Commission from deals still being reviewed — not guaranteed yet, but on track.">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Pending</p>
          <p className="font-serif text-xl text-igc-goldText">${summary.commission_totals.total_pending.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="rep-financial-paid" title="Commission that has actually already been sent to you in past pay cycles — real money already paid.">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Paid Out</p>
          <p className="font-serif text-xl text-igc-cream">${summary.commission_totals.total_paid.toLocaleString()}</p>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3" title="A running list of every commission you've earned, one row per deal, with its current payout status.">Commission History</h2>
        {summary.commission_history.length === 0 ? (
          <p className="text-igc-muted text-sm" data-testid="rep-financial-no-commissions">No commissions logged yet.</p>
        ) : (
          <div className="bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10" data-testid="rep-financial-commission-list">
            {summary.commission_history.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`rep-commission-row-${c.id}`}>
                <div>
                  <span className="text-igc-cream capitalize">{c.deal_type === "trust" ? (c.trust_product === "basic" ? "Basic Living Trust" : "Private Trust") : "Funding Deal"}</span>
                  <span className="text-igc-muted text-xs ml-2">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 capitalize ${c.status === "paid" ? "bg-igc-success/10 text-igc-success" : c.status === "rejected" ? "bg-igc-danger/10 text-igc-danger" : "bg-igc-gold/15 text-igc-goldText"}`} title="Whether this commission has been paid out, is still awaiting approval, or was not approved.">{c.status === "pending" ? "Awaiting Approval" : c.status}</span>
                  <span className="font-serif text-igc-cream">${c.amount.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3" title="Log your own business expenses here (mileage, phone bill, home office, etc.) so they're subtracted from your income when estimating what you owe in taxes below.">Log an Expense</h2>
        <form onSubmit={submitExpense} className="bg-igc-surface border border-igc-gold/15 p-4 flex flex-wrap gap-3 items-end">
          <div className="w-40">
            <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
              <SelectTrigger data-testid="rep-expense-category" title="What kind of business expense this is — mileage for driving, phone for your work phone bill, home office for a portion of rent/utilities, or other." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <Input data-testid="rep-expense-amount" type="number" min="0" step="0.01" placeholder="Amount" title="The dollar amount of this expense." value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Input data-testid="rep-expense-description" placeholder="Description (optional)" title="A short note about what this expense was for, to help you remember it later — optional." value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          </div>
          <Button data-testid="rep-expense-submit-button" type="submit" disabled={savingExpense} title="Saves this expense to your log below and updates your tax estimate." className="bg-igc-gold hover:bg-igc-goldHover text-black">
            <CurrencyDollar size={16} weight="bold" className="mr-1" /> Log Expense
          </Button>
        </form>

        {summary.expenses.length > 0 && (
          <div className="mt-3 bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10" data-testid="rep-expense-list">
            {summary.expenses.map((ex) => (
              <div key={ex.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`rep-expense-row-${ex.id}`}>
                <div>
                  <span className="text-igc-cream capitalize">{ex.category.replace("_", " ")}</span>
                  {ex.description && <span className="text-igc-muted text-xs ml-2">{ex.description}</span>}
                  <span className="text-igc-muted text-xs ml-2">{new Date(ex.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-serif text-igc-cream">${ex.amount.toLocaleString()}</span>
                  <button data-testid={`rep-expense-delete-${ex.id}`} onClick={() => deleteExpense(ex.id)} title="Permanently removes this expense from your log. This will also reduce your tax write-offs, so only delete it if you logged it by mistake." className="text-igc-muted hover:text-igc-danger">
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3" title="A rough, automatic estimate of what you might owe in self-employment tax based on your commissions and logged expenses, so there are no surprises at tax time.">Self-Employment Tax Estimate</h2>
        <div className="bg-igc-surface border border-igc-gold/15 p-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="rep-tax-estimate-card">
          <div>
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="Every dollar of commission you've earned before any write-offs are subtracted.">Gross Commission</p>
            <p className="font-serif text-lg text-igc-cream">${summary.tax_estimate.gross_commission.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="The total of every expense you've logged above (mileage, phone, home office, etc.) — these reduce the income you're taxed on.">Total Expenses</p>
            <p className="font-serif text-lg text-igc-cream">${summary.tax_estimate.total_expenses.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="Gross Commission minus Total Expenses — this is the amount that's actually subject to tax.">Net Taxable</p>
            <p className="font-serif text-lg text-igc-cream">${summary.tax_estimate.net_taxable.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="An estimate of self-employment tax (Social Security + Medicare, 15.3%) on your Net Taxable amount. This is only an estimate for planning — it is not tax advice.">Est. SE Tax (15.3%)</p>
            <p className="font-serif text-lg text-igc-goldText">${summary.tax_estimate.estimated_se_tax.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-igc-muted text-[11px] mt-2">This is an estimate for planning purposes only, not tax advice — consult a tax professional for filing.</p>
        <div className="flex gap-3 mt-4">
          <button data-testid="rep-tax-summary-download-pdf" onClick={() => downloadTaxSummary("pdf")} title="Downloads a PDF document summarizing your commission and expenses for this year — useful to hand to your accountant at tax time." className="flex items-center gap-2 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 px-4 py-2 text-sm transition-colors">
            <FilePdf size={16} weight="bold" /> Year-End Tax Summary (PDF)
          </button>
          <button data-testid="rep-tax-summary-download-csv" onClick={() => downloadTaxSummary("csv")} title="Downloads a spreadsheet (CSV) file with the same commission and expense data as the PDF, useful for importing into accounting software." className="flex items-center gap-2 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 px-4 py-2 text-sm transition-colors">
            <FileCsv size={16} weight="bold" /> Year-End Tax Summary (CSV)
          </button>
        </div>
      </div>
    </div>
  );
}
